import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CalendarDays, Clock, Check, ArrowRight } from "lucide-react";
import { CONTACT, TIME_SLOTS } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.16, 1, 0.3, 1];

const todayStr = () => new Date().toISOString().split("T")[0];

export const Booking = () => {
  const [form, setForm] = useState({ name: "", email: "", organisation: "", date: todayStr(), time: "", notes: "" });
  const [taken, setTaken] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!form.date) return;
    axios.get(`${API}/bookings/taken?date=${form.date}`).then((r) => setTaken(r.data.taken || [])).catch(() => setTaken([]));
  }, [form.date]);

  useEffect(() => {
    const h = (e) => setForm((f) => ({ ...f, notes: e.detail }));
    window.addEventListener("tpj-prefill", h);
    return () => window.removeEventListener("tpj-prefill", h);
  }, []);

  const submit = async (e) => {    e.preventDefault();
    if (!form.name || !form.email || !form.date || !form.time) {
      toast.error("Please add your name, email, a date and a time slot.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
      toast.success("Booking received — check your inbox for confirmation.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="relative py-24 md:py-36" data-testid="booking-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease }}
          className="font-serif text-6xl md:text-[9vw] leading-[0.85] tracking-tight mb-14 md:mb-20"
        >
          Let's <span className="italic text-terra">talk.</span>
        </motion.h2>
      </div>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-terra mb-5">Schedule a Consultation</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight">
            A free 30-minute <span className="italic text-terra">call</span>
            <span className="block mt-4 text-2xl md:text-3xl leading-snug italic text-terra">
              Tell us about your priorities and explore how we can support your esteemed organisation.
            </span>
          </h2>
          <div className="mt-10">
            <p className="text-xs tracking-[0.22em] uppercase font-semibold text-terra mb-3">Our Contact Details</p>
            <div className="space-y-2 text-ink/70">
              <p>
                <span className="text-ink/45">Email</span> ·{" "}
                <a href={`mailto:${CONTACT.email}`} className="font-semibold text-terra hover:text-ink transition-colors" data-testid="booking-email-link">
                  {CONTACT.email}
                </a>
              </p>
              <p>
                <span className="text-ink/45">Phone</span> ·{" "}
                <a href={`tel:${CONTACT.phoneHref}`} className="font-semibold text-terra hover:text-ink transition-colors" data-testid="booking-phone-link">
                  {CONTACT.phone}
                </a>
              </p>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
          className="lg:col-span-7"
        >
          {done ? (
            <div data-testid="booking-success" className="border border-ink/15 bg-sand/40 p-10 md:p-14 h-full flex flex-col justify-center">
              <div className="w-14 h-14 bg-terra flex items-center justify-center mb-6"><Check className="w-7 h-7 text-white" /></div>
              <h3 className="font-serif text-3xl md:text-4xl">You're booked in.</h3>
              <p className="mt-4 text-ink/65 md:text-lg max-w-md">
                We've received your request for <strong>{form.date}</strong> at <strong>{form.time}</strong> and sent a confirmation to {form.email}. We'll be in touch shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="booking-form" className="border border-ink/15 bg-bone p-8 md:p-12 space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <label className="block">
                  <span className="text-xs tracking-[0.15em] uppercase text-ink/50">Your name</span>
                  <input value={form.name} onChange={set("name")} data-testid="booking-name" placeholder="Jane Doe"
                    className="w-full bg-transparent border-b border-ink/25 py-3 text-lg outline-none focus:border-terra transition-colors placeholder:text-ink/30" />
                </label>
                <label className="block">
                  <span className="text-xs tracking-[0.15em] uppercase text-ink/50">Email</span>
                  <input type="email" value={form.email} onChange={set("email")} data-testid="booking-email" placeholder="jane@school.edu"
                    className="w-full bg-transparent border-b border-ink/25 py-3 text-lg outline-none focus:border-terra transition-colors placeholder:text-ink/30" />
                </label>
              </div>
              <label className="block">
                <span className="text-xs tracking-[0.15em] uppercase text-ink/50">School / Organisation</span>
                <input value={form.organisation} onChange={set("organisation")} data-testid="booking-organisation" placeholder="Your school"
                  className="w-full bg-transparent border-b border-ink/25 py-3 text-lg outline-none focus:border-terra transition-colors placeholder:text-ink/30" />
              </label>

              <label className="block">
                <span className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-ink/50"><CalendarDays className="w-4 h-4" /> Preferred date</span>
                <input type="date" min={todayStr()} value={form.date} onChange={set("date")} data-testid="booking-date"
                  className="w-full bg-transparent border-b border-ink/25 py-3 text-lg outline-none focus:border-terra transition-colors" />
              </label>

              <div>
                <span className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-ink/50 mb-3"><Clock className="w-4 h-4" /> Preferred time</span>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5">
                  {TIME_SLOTS.map((t) => {
                    const isTaken = taken.includes(t);
                    const active = form.time === t;
                    return (
                      <button
                        type="button"
                        key={t}
                        disabled={isTaken}
                        onClick={() => setForm((f) => ({ ...f, time: t }))}
                        data-testid={`slot-${t}`}
                        className={`py-2.5 text-sm border transition-all duration-200 ${
                          isTaken ? "border-ink/10 text-ink/25 line-through cursor-not-allowed"
                          : active ? "bg-terra text-white border-terra"
                          : "border-ink/25 text-ink hover:border-terra hover:text-terra"
                        }`}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              <label className="block">
                <span className="text-xs tracking-[0.15em] uppercase text-ink/50">Anything we should know?</span>
                <textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="booking-notes" placeholder="Optional"
                  className="w-full bg-transparent border-b border-ink/25 py-3 text-lg outline-none focus:border-terra transition-colors resize-none placeholder:text-ink/30" />
              </label>

              <button type="submit" disabled={loading} data-testid="booking-submit"
                className="group bg-terra text-white px-8 py-4 text-sm tracking-wide flex items-center gap-3 hover:shadow-[6px_6px_0_0_#14283D] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60">
                {loading ? "Scheduling..." : "Schedule a consultation"}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
